import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useContent } from 'src/contexts/ContentContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { ContentId } from 'src/utils/constants/contentId';
import { roundToPrecision } from 'src/utils/numberFormatter';
import {
  ListingGroupAutoPriceSettings,
  UiCurrency,
} from 'src/WebApiController';

import * as styles from './PricingSetting.css';

enum RankPremiumMode {
  Absolute = 'Absolute',
  Relative = 'Relative',
}

const RANK_PREMIUM_TO_SYMBOL_MAP = {
  [RankPremiumMode.Absolute]: '$',
  [RankPremiumMode.Relative]: '%',
};

interface PricingSettingAutoPriceRankPremiumSettingsProps {
  uiCurrency: UiCurrency;
}

export const PricingSettingAutoPriceRankPremiumSettings = ({
  uiCurrency,
}: PricingSettingAutoPriceRankPremiumSettingsProps) => {
  const addRankPremiumText = useContent(
    ContentId.GroupListingsSettingRankPremium
  );
  const [premiumMode, setPremiumMode] = useState<RankPremiumMode>(
    RankPremiumMode.Absolute
  );
  const { watch, setValue } = useFormContext<ListingGroupAutoPriceSettings>();

  const undAbsAmt = watch('undAbsAmt');
  const undRelAmt = watch('undRelAmt');

  useEffect(() => {
    if (undRelAmt != null) {
      setPremiumMode((prev) =>
        prev !== RankPremiumMode.Relative ? RankPremiumMode.Relative : prev
      );
    } else if (undAbsAmt != null) {
      setPremiumMode((prev) =>
        prev !== RankPremiumMode.Absolute ? RankPremiumMode.Absolute : prev
      );
    }
  }, [undAbsAmt, undRelAmt]);

  const currentValue =
    premiumMode === RankPremiumMode.Absolute
      ? undAbsAmt
      : undRelAmt != null
        ? roundToPrecision(Math.abs(undRelAmt) * 100, 8)
        : '';

  const handleValueChange = (value: string) => {
    const numericValue = parseFloat(value);

    if (!isNaN(numericValue)) {
      if (premiumMode === RankPremiumMode.Absolute) {
        setValue('undAbsAmt', Math.max(numericValue, 0));
        setValue('undRelAmt', null);
      } else {
        const clampedValue = Math.min(Math.max(numericValue, 0), 100) / 100;
        setValue('undAbsAmt', null);
        setValue('undRelAmt', clampedValue);
      }
    }
  };

  return (
    <div className={styles.rankPremiumContainer}>
      <PosCurrencyField
        rootProps={{
          className: styles.rankPremiumValueInputWrapper,
        }}
        className={styles.textAlignRight}
        uiCurrency={uiCurrency}
        prefixDisplay={
          <span className={styles.setPriceText}>{addRankPremiumText}:</span>
        }
        value={currentValue ?? undefined}
        onChange={(e) => handleValueChange(e.target.value)}
      />
      <div className={styles.rankPremiumTypeInputWrapper}>
        <PosEnumSelect
          value={premiumMode}
          valueOptionsContent={RANK_PREMIUM_TO_SYMBOL_MAP}
          enableEmptySelection={false}
          className={clsx(styles.inputInline, styles.rankPremSelectContainer)}
          triggerContentClassName={styles.selectTriggerContent}
          onClick={(e) => e.stopPropagation()}
          onChange={(v) => setPremiumMode(v as RankPremiumMode)}
        />
      </div>
    </div>
  );
};
